import React, { useState } from 'react';
import { placeBet } from '../api/api';

export interface BetItem {
  id: string;
  matchId: string | number;
  matchName: string;
  betType: 'home' | 'draw' | 'away';
  tippLabel: string;
  odds: string;
  // API fields
  eventId?: string;
  marketId?: string;
  outcomeId?: string;
  specifiers?: string;
}

interface BettingSlipDrawerProps {
  bets: BetItem[];
  onRemoveBet: (id: string) => void;
  onClearAll: () => void;
  onClose: () => void;
  onLoginClick: () => void;
}

const BettingSlipDrawer: React.FC<BettingSlipDrawerProps> = ({
  bets,
  onRemoveBet,
  onClearAll,
  onClose,
  onLoginClick,
}) => {
  const [stake, setStake] = useState('5');
  const [placing, setPlacing] = useState(false);
  const [betStatus, setBetStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [betError, setBetError] = useState('');

  const isLoggedIn = !!localStorage.getItem('access_token');

  const totalOdds = bets.reduce((acc, bet) => {
    const val = parseFloat(bet.odds.replace(',', '.'));
    return acc * val;
  }, 1);

  const stakeNum = parseFloat(stake) || 0;
  const possibleWin = (stakeNum * totalOdds).toFixed(2).replace('.', ',');

  const handlePlaceBet = async () => {
    const selections = bets
      .filter((b) => b.eventId && b.marketId && b.outcomeId)
      .map((b) => ({
        event_id: b.eventId!,
        market_id: b.marketId!,
        outcome_id: b.outcomeId!,
        ...(b.specifiers ? { specifiers: b.specifiers } : {}),
      }));
    if (selections.length === 0) {
      setBetError('Keine gültigen Auswahlen gefunden.');
      setBetStatus('error');
      return;
    }
    setPlacing(true);
    setBetStatus('idle');
    setBetError('');
    try {
      await placeBet({ ticket_type: 'single', stake: stakeNum, selections });
      setBetStatus('success');
      onClearAll();
    } catch (err: any) {
      setBetError(err.message || 'Fehler beim Platzieren der Wette.');
      setBetStatus('error');
    } finally {
      setPlacing(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed bottom-0 left-0 right-0 z-50 flex flex-col max-h-[85vh]">
        {/* Red Header */}
        <div className="bg-tipico-red px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <span className="text-white text-2xl font-bold">Wettschein</span>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full border-2 border-white flex items-center justify-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="bg-white flex-1 overflow-y-auto">
          {bets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <span className="text-4xl mb-3">📋</span>
              <p className="text-gray-500 text-sm">Noch keine Wetten ausgewählt.</p>
              <p className="text-gray-400 text-xs mt-1">Wähle einen Tipp aus um zu beginnen.</p>
            </div>
          ) : (
            <>
              {/* Einzel Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <span className="text-sm font-semibold text-gray-800">Einzel</span>
                <button onClick={onClearAll} className="text-gray-400 hover:text-red-500 transition">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              {/* Bet Items */}
              {bets.map((bet) => (
                <div key={bet.id} className="px-4 py-4 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 pr-3">
                      <p className="text-sm text-gray-700">
                        Tipp: <span className="font-semibold text-gray-900">{bet.tippLabel}</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{bet.matchName}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-800">
                        {bet.odds}
                      </span>
                      <button
                        onClick={() => onRemoveBet(bet.id)}
                        className="text-gray-400 hover:text-red-500 transition"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        {bets.length > 0 && (
          <div className="bg-white border-t border-gray-200 px-4 pt-4 pb-6">
            <div className="flex gap-3 mb-4">
              {/* Stake Input */}
              <div className="flex-1 border border-gray-300 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-500 mb-1">Gesamteinsatz</p>
                <div className="flex items-center">
                  <input
                    type="number"
                    value={stake}
                    onChange={(e) => setStake(e.target.value)}
                    className="text-base font-bold text-gray-800 w-full focus:outline-none"
                    min="0"
                  />
                  <span className="text-sm font-bold text-gray-800 ml-1">€</span>
                </div>
              </div>

              {/* Possible Win */}
              <div className="flex-1 px-3 py-2">
                <p className="text-xs text-gray-500 mb-1">Möglicher Gewinn</p>
                <p className="text-base font-bold text-green-600">{possibleWin} €</p>
              </div>
            </div>

            {betStatus === 'success' && (
              <p className="text-green-600 text-sm text-center mb-3 font-medium">Wette erfolgreich platziert! ✓</p>
            )}
            {betStatus === 'error' && (
              <p className="text-red-600 text-sm text-center mb-3">{betError}</p>
            )}

            {isLoggedIn ? (
              <button
                onClick={handlePlaceBet}
                disabled={placing || stakeNum <= 0}
                className="w-full bg-tipico-red text-white font-semibold py-4 rounded-lg text-base hover:bg-tipico-red-dark transition disabled:opacity-60"
              >
                {placing ? 'Wird platziert…' : 'Wette platzieren'}
              </button>
            ) : (
              <button
                onClick={onLoginClick}
                className="w-full bg-gray-900 text-white font-semibold py-4 rounded-lg text-base hover:bg-gray-700 transition"
              >
                Anmelden
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default BettingSlipDrawer;
